import { useAsync, useErc20Details } from '@arcxmoney/arcx-react'
import { BigNumber, constants, utils } from 'ethers'
import React, { useContext, useState } from 'react'
import ReactTooltip from 'react-tooltip'
import { ArcContext } from 'src/contexts/ArcContext'
import { useErc20Balance } from 'src/hooks/useErc20Balance'
import {
  curvePoolAddresses,
  CurveService,
  PoolInfo,
  stableTokenAddresses,
} from 'src/services/CurveService'
import { normalizeToWei } from 'src/utils/normalizeToWei'
import useDebounce from 'src/utils/useDebounce'
import { ArcButton } from '../ArcButton'
import { CurveNotice } from '../CurveNotice'
import { Input } from '../Input'
import './Swap.scss'
import { SwapInfo } from './SwapInfo'

const curvePoolTokens = Object.keys(curvePoolAddresses)
const curvePoolAssets = curvePoolTokens.map((erc20Name) => ({
  label: erc20Name,
  value: erc20Name,
}))

interface IProps {
  curveService: CurveService
  poolInfo: PoolInfo
}

export function Swap({ curveService, poolInfo }: IProps) {
  const { isLoading, setIsLoading } = useContext(ArcContext)
  const [swapFromValue, setSwapFromValue] = useState(constants.Zero)
  const [from, setFrom] = useState<string>('STABLEX')
  const [to, setTo] = useState<string>(curvePoolTokens[0])

  const debouncedSwapFromValue = useDebounce(swapFromValue, 1000)

  const [expectedAmount] = useAsync<BigNumber>(async () => {
    if (swapFromValue.isZero()) {
      return constants.Zero
    }
    const expectedAmount = await curveService.getExpectedSwappedAmount(
      curvePoolAddresses[from],
      curvePoolAddresses[to],
      debouncedSwapFromValue,
    )
    return expectedAmount
  }, [debouncedSwapFromValue, to, from])

  const balanceFrom = useErc20Balance(curvePoolAddresses[from], curveService.signer)
  const balanceTo = useErc20Balance(curvePoolAddresses[to], curveService.signer)
  const fromTokenDetails = useErc20Details(curvePoolAddresses[from], curveService.signer)
  const toTokenDetails = useErc20Details(curvePoolAddresses[to], curveService.signer)

  const stableDepositedPercentage =
    (fromTokenDetails &&
      toTokenDetails &&
      expectedAmount &&
      calcTargetStablePercentage(
        poolInfo,
        normalizeToWei(swapFromValue, fromTokenDetails?.decimals),
        from,
        normalizeToWei(expectedAmount, toTokenDetails?.decimals),
        to,
      )) ||
    constants.Zero

  const isActionDisabled =
    balanceFrom.lt(debouncedSwapFromValue) ||
    debouncedSwapFromValue.isZero() ||
    !expectedAmount ||
    from === to ||
    !debouncedSwapFromValue.eq(swapFromValue) ||
    !curveService.isPoolStable(stableDepositedPercentage)

  // const swapTokens = () => {
  //   setTo(from)
  //   setFrom(to)
  //   setTimeout(() => setSwapFromValue(expectedAmount), 50)
  // }
  return (
    <div className="swap">
      <Input
        balance={balanceFrom}
        decimals={fromTokenDetails?.decimals}
        label="You pay"
        assets={curvePoolAssets}
        onAssetChanged={setFrom}
        setAmount={setSwapFromValue}
        selectedAsset={from}
        maxAmount={balanceFrom}
      />
      {/* <img onClick={swapTokens} className="swap__icon" alt="swap icon" src={ExchangeIcon} /> */}
      <Input
        balance={balanceTo}
        decimals={toTokenDetails?.decimals}
        label="You receive"
        assets={curvePoolAssets}
        onAssetChanged={setTo}
        selectedAsset={to}
        maxAmount={expectedAmount}
        defaultValue={expectedAmount}
        disabled={true} // eslint-disable-line react/jsx-boolean-value
        loading={!expectedAmount || !toTokenDetails}
      />
      <SwapInfo poolInfo={poolInfo} />
      <ReactTooltip disable={!isActionDisabled} id="swap__disabled-arc-button" />
      <span
        data-for="swap__disabled-arc-button"
        data-place="top"
        data-tip={`The pool cannot be imbalanced by more than ${curveService.config.minimalPoolPartPercentage}%`}
        data-effect="solid"
        className="swap__tooltip"
      >
        <ArcButton
          onClick={async () => {
            try {
              setIsLoading(true)
              await curveService.swap(
                curvePoolAddresses[from],
                curvePoolAddresses[to],
                debouncedSwapFromValue,
                expectedAmount,
              )
            } finally {
              setIsLoading(false)
            }
          }}
          name="Swap"
          disabled={isActionDisabled}
          loading={isLoading}
        />
      </span>
      <CurveNotice />
    </div>
  )
}

function calcTargetStablePercentage(
  poolInfo: PoolInfo,
  normalizedFromValue: BigNumber,
  from: string,
  normalizedToValue: BigNumber,
  to: string,
) {
  let targetStablePoolDeposited = BigNumber.from(poolInfo.tokenDeposited)
  let targetThreePoolDeposited = BigNumber.from(poolInfo.threePoolDeposited)
  if (stableTokenAddresses[from]) {
    targetStablePoolDeposited = targetStablePoolDeposited.add(normalizedFromValue)
  } else {
    targetThreePoolDeposited = targetThreePoolDeposited.add(normalizedFromValue)
  }
  if (stableTokenAddresses[to]) {
    targetStablePoolDeposited = targetStablePoolDeposited.sub(normalizedToValue)
  } else {
    targetThreePoolDeposited = targetThreePoolDeposited.sub(normalizedToValue)
  }
  return utils
    .parseEther('100')
    .mul(targetStablePoolDeposited)
    .div(targetStablePoolDeposited.add(targetThreePoolDeposited))
}
